import { useState, useEffect, useCallback } from 'react';
import { Users, BookOpen, Star, BarChart3, Trash2, PauseCircle, PlayCircle, XCircle, ChevronDown, ChevronUp, CreditCard, FileText, Share2, Flag, ShieldBan, ShieldCheck, CheckCircle, Download, ExternalLink, AlertTriangle, TrendingUp, Mail, Facebook, DollarSign } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import AnalyticsTab from './AnalyticsTab';
import DripsTab from './DripsTab';
import SocialTab from './SocialTab';
import PayoutsTab from './PayoutsTab';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'books', label: 'Books', icon: BookOpen },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'questionnaires', label: 'Questionnaires', icon: FileText },
  { id: 'crossposts', label: 'Cross-Posts', icon: Share2 },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  { id: 'drips', label: 'Email Drips', icon: Mail },
  { id: 'social', label: 'Social', icon: Facebook },
  { id: 'payouts', label: 'Payouts', icon: DollarSign },
];

export default function AdminPanel() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [books, setBooks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [crossPosts, setCrossPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedQ, setExpandedQ] = useState(null);
  const [flagModal, setFlagModal] = useState(null);
  const [banModal, setBanModal] = useState(null);
  const [reasonText, setReasonText] = useState('');

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [s, m, b, r, q, cp] = await Promise.all([
        api.get('/admin/stats', { headers }),
        api.get('/admin/club/members', { headers }),
        api.get('/admin/club/books', { headers }),
        api.get('/admin/club/reviews', { headers }),
        api.get('/admin/questionnaires', { headers }),
        api.get('/admin/cross-posts', { headers }),
      ]);
      setStats(s.data);
      setMembers(m.data);
      setBooks(b.data);
      setReviews(r.data);
      setQuestionnaires(q.data);
      setCrossPosts(cp.data);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Admin access required.' : 'Failed to load data.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const adjustCredits = async (memberId, amount, reason) => {
    try {
      await api.put(`/admin/club/members/${memberId}/credits`, { amount, reason }, { headers });
      fetchData();
    } catch { setError('Failed to adjust credits'); }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this member?')) return;
    try { await api.delete(`/admin/club/members/${id}`, { headers }); fetchData(); }
    catch { setError('Failed to remove member'); }
  };

  const updateBookStatus = async (id, status) => {
    try { await api.put(`/admin/club/books/${id}/status`, { status }, { headers }); fetchData(); }
    catch { setError('Failed to update book'); }
  };

  const removeBook = async (id) => {
    if (!window.confirm('Remove this book?')) return;
    try { await api.delete(`/admin/club/books/${id}`, { headers }); fetchData(); }
    catch { setError('Failed to remove book'); }
  };

  const updateReviewStatus = async (id, status) => {
    try { await api.put(`/admin/club/reviews/${id}/status`, { status }, { headers }); fetchData(); }
    catch { setError('Failed to update review'); }
  };

  const flagReview = async (reviewId, flagged, reason) => {
    try {
      await api.put(`/admin/club/reviews/${reviewId}/flag`, { flagged, reason }, { headers });
      setFlagModal(null);
      setReasonText('');
      fetchData();
    } catch { setError('Failed to flag review'); }
  };

  const banMember = async (memberId, banned, reason) => {
    try {
      await api.put(`/admin/club/members/${memberId}/ban`, { banned, reason }, { headers });
      setBanModal(null);
      setReasonText('');
      fetchData();
    } catch { setError('Failed to ban member'); }
  };

  const syncCrossPost = async (postId) => {
    try {
      await api.put(`/admin/cross-posts/${postId}/sync`, { synced: true, target: 'upost' }, { headers });
      fetchData();
    } catch { setError('Failed to sync cross-post'); }
  };

  const exportCrossPost = async (postId) => {
    try {
      const res = await api.get(`/admin/cross-posts/export/${postId}`, { headers });
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upost-import-${data.slug}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to export cross-post'); }
  };

  if (!token) return <section className="pt-40 text-center"><p className="text-muted-foreground">Please log in as admin.</p></section>;
  if (error === 'Admin access required.') return <section className="pt-40 text-center"><p className="text-red-400">{error}</p></section>;

  return (
    <>
      <SeoHead title="Admin Panel — AI Ghostwriter Pro" noindex={true} />
      <section className="pt-28 pb-20 md:pt-36 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <h1 className="font-serif text-2xl md:text-3xl tracking-tight mb-6" data-testid="admin-title">
            Admin <span className="text-primary">Panel</span>
          </h1>

          {error && error !== 'Admin access required.' && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-sm p-3 mb-4">{error}</p>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto" data-testid="admin-tabs">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {loading && <p className="text-muted-foreground text-sm">Loading...</p>}

          {/* Overview */}
          {tab === 'overview' && stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-stats">
              {[
                { label: 'Club Members', value: stats.club_members, icon: Users },
                { label: 'Books Listed', value: stats.club_books, icon: BookOpen },
                { label: 'Reviews', value: stats.club_reviews, icon: Star },
                { label: 'Registered Users', value: stats.users, icon: Users },
                { label: 'GW Projects', value: stats.projects, icon: CreditCard },
                { label: 'Paid Projects', value: stats.paid_projects, icon: CreditCard },
                { label: 'Newsletter Subs', value: stats.newsletter_subscribers, icon: Users },
                { label: 'Questionnaires', value: stats.questionnaires, icon: FileText },
                { label: 'Flagged Reviews', value: stats.flagged_reviews, icon: Flag, highlight: stats.flagged_reviews > 0 ? 'amber' : null },
                { label: 'Banned Members', value: stats.banned_members, icon: ShieldBan, highlight: stats.banned_members > 0 ? 'red' : null },
                { label: 'Pending Cross-Posts', value: stats.pending_cross_posts, icon: Share2, highlight: stats.pending_cross_posts > 0 ? 'blue' : null },
              ].map((s, i) => (
                <div key={i} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`bg-white/5 border rounded-sm p-5 ${s.highlight === 'amber' ? 'border-amber-500/30' : s.highlight === 'red' ? 'border-red-500/30' : s.highlight === 'blue' ? 'border-blue-500/30' : 'border-white/10'}`}>
                  <s.icon className={`w-4 h-4 mb-2 ${s.highlight === 'amber' ? 'text-amber-400' : s.highlight === 'red' ? 'text-red-400' : s.highlight === 'blue' ? 'text-blue-400' : 'text-primary'}`} />
                  <p className={`font-serif text-2xl ${s.highlight === 'amber' ? 'text-amber-400' : s.highlight === 'red' ? 'text-red-400' : s.highlight === 'blue' ? 'text-blue-400' : 'text-primary'}`}>{s.value}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Members */}
          {tab === 'members' && (
            <div className="space-y-2" data-testid="admin-members">
              <p className="text-xs text-muted-foreground mb-4">{members.length} members</p>
              {members.map(m => (
                <div key={m.id} className={`bg-white/5 border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${m.banned ? 'border-red-500/30 bg-red-500/5' : 'border-white/10'}`} data-testid={`member-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm truncate">
                      {m.name} <span className="text-muted-foreground text-xs">({m.email})</span>
                      {m.banned && <span className="ml-2 text-[10px] font-sans font-bold uppercase tracking-wider text-red-400 bg-red-500/10 px-2 py-0.5 rounded-sm">Banned</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {m.credits} credits &middot; {m.total_reviews} reviews &middot; {m.total_books} books
                      {m.is_subscriber ? <span className="text-primary"> Subscriber</span> : ''}
                    </p>
                    {m.banned && m.ban_reason && <p className="text-[10px] text-red-400/70 mt-1">Reason: {m.ban_reason}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => adjustCredits(m.id, 50, 'Admin bonus')} className="text-xs text-green-400 hover:underline" data-testid={`add-credits-${m.id}`}>+50</button>
                    <button onClick={() => adjustCredits(m.id, -50, 'Admin deduction')} className="text-xs text-amber-400 hover:underline">-50</button>
                    {m.banned ? (
                      <button onClick={() => banMember(m.id, false, '')} className="text-xs text-green-400 hover:underline flex items-center gap-1" data-testid={`unban-${m.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Unban
                      </button>
                    ) : (
                      <button onClick={() => { setBanModal(m.id); setReasonText(''); }} className="text-xs text-red-400 hover:underline flex items-center gap-1" data-testid={`ban-${m.id}`}>
                        <ShieldBan className="w-3.5 h-3.5" /> Ban
                      </button>
                    )}
                    <button onClick={() => removeMember(m.id)} className="text-red-400 hover:text-red-300" data-testid={`remove-member-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}

              {/* Ban Modal */}
              {banModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="ban-modal">
                  <div className="bg-card border border-white/10 rounded-sm p-6 w-full max-w-md">
                    <h3 className="font-serif text-lg mb-4">Ban Member</h3>
                    <p className="text-sm text-muted-foreground mb-3">Provide a reason for banning this member:</p>
                    <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="Reason for ban..."
                      className="w-full bg-white/5 border border-white/10 rounded-sm p-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none mb-4 resize-none h-24"
                      data-testid="ban-reason-input" />
                    <div className="flex gap-3 justify-end">
                      <button onClick={() => { setBanModal(null); setReasonText(''); }} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground" data-testid="ban-cancel">Cancel</button>
                      <button onClick={() => banMember(banModal, true, reasonText)} className="px-4 py-2 text-sm bg-red-500/20 text-red-400 border border-red-500/30 rounded-sm hover:bg-red-500/30" data-testid="ban-confirm">Ban Member</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Books */}
          {tab === 'books' && (
            <div className="space-y-2" data-testid="admin-books">
              <p className="text-xs text-muted-foreground mb-4">{books.length} books</p>
              {books.map(b => (
                <div key={b.id} className="bg-white/5 border border-white/10 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`book-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm truncate">{b.title} <span className="text-muted-foreground text-xs">by {b.author}</span></p>
                    <p className="text-xs text-muted-foreground">{b.genre} &middot; {b.reviews_received} reviews &middot; {b.credits_per_review} cred/review &middot;
                      <span className={b.status === 'active' ? 'text-green-400' : b.status === 'paused' ? 'text-amber-400' : 'text-red-400'}> {b.status}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {b.status === 'active' ? (
                      <button onClick={() => updateBookStatus(b.id, 'paused')} className="text-amber-400 hover:text-amber-300" title="Pause"><PauseCircle className="w-4 h-4" /></button>
                    ) : (
                      <button onClick={() => updateBookStatus(b.id, 'active')} className="text-green-400 hover:text-green-300" title="Activate"><PlayCircle className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => removeBook(b.id)} className="text-red-400 hover:text-red-300" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reviews */}
          {tab === 'reviews' && (
            <div className="space-y-2" data-testid="admin-reviews">
              <p className="text-xs text-muted-foreground mb-4">{reviews.length} reviews</p>
              {reviews.map(r => (
                <div key={r.id} className={`bg-white/5 border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${r.flagged ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/10'}`} data-testid={`review-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm truncate">
                      {r.book_title}
                      {r.flagged && <span className="ml-2 text-[10px] font-sans font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-sm">Flagged</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">by {r.reviewer_name} &middot; {r.credits_awarded} credits &middot;
                      <span className={r.status === 'verified' ? 'text-green-400' : r.status === 'rejected' ? 'text-red-400' : 'text-amber-400'}> {r.status}</span>
                    </p>
                    {r.flagged && r.flag_reason && <p className="text-[10px] text-amber-400/70 mt-1">Flag reason: {r.flag_reason}</p>}
                    <a href={r.amazon_review_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline">View on Amazon</a>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.status !== 'verified' && (
                      <button onClick={() => updateReviewStatus(r.id, 'verified')} className="text-xs text-green-400 hover:underline" data-testid={`approve-review-${r.id}`}>Approve</button>
                    )}
                    {r.status !== 'rejected' && (
                      <button onClick={() => updateReviewStatus(r.id, 'rejected')} className="text-xs text-red-400 hover:underline flex items-center gap-1" data-testid={`reject-review-${r.id}`}><XCircle className="w-3 h-3" /> Reject</button>
                    )}
                    {r.flagged ? (
                      <button onClick={() => flagReview(r.id, false, '')} className="text-xs text-green-400 hover:underline flex items-center gap-1" data-testid={`unflag-review-${r.id}`}>
                        <CheckCircle className="w-3 h-3" /> Unflag
                      </button>
                    ) : (
                      <button onClick={() => { setFlagModal(r.id); setReasonText(''); }} className="text-xs text-amber-400 hover:underline flex items-center gap-1" data-testid={`flag-review-${r.id}`}>
                        <Flag className="w-3 h-3" /> Flag
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Flag Modal */}
              {flagModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="flag-modal">
                  <div className="bg-card border border-white/10 rounded-sm p-6 w-full max-w-md">
                    <h3 className="font-serif text-lg mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Flag Review</h3>
                    <p className="text-sm text-muted-foreground mb-3">Provide a reason for flagging this review:</p>
                    <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="Reason for flagging..."
                      className="w-full bg-white/5 border border-white/10 rounded-sm p-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none mb-4 resize-none h-24"
                      data-testid="flag-reason-input" />
                    <div className="flex gap-3 justify-end">
                      <button onClick={() => { setFlagModal(null); setReasonText(''); }} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground" data-testid="flag-cancel">Cancel</button>
                      <button onClick={() => flagReview(flagModal, true, reasonText)} className="px-4 py-2 text-sm bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-sm hover:bg-amber-500/30" data-testid="flag-confirm">Flag Review</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Questionnaires */}
          {tab === 'questionnaires' && (
            <div className="space-y-2" data-testid="admin-questionnaires">
              <p className="text-xs text-muted-foreground mb-4">{questionnaires.length} questionnaires</p>
              {questionnaires.map(q => (
                <div key={q.id} className="bg-white/5 border border-white/10 rounded-sm" data-testid={`q-${q.id}`}>
                  <button onClick={() => setExpandedQ(expandedQ === q.id ? null : q.id)} className="w-full p-4 flex items-center justify-between text-left">
                    <div>
                      <p className="font-serif text-sm">{q.genre}{q.subgenre ? ` — ${q.subgenre}` : ''}</p>
                      <p className="text-xs text-muted-foreground">{q.target_audience} &middot; {q.tone} &middot; {new Date(q.created_at).toLocaleDateString()}</p>
                    </div>
                    {expandedQ === q.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  {expandedQ === q.id && (
                    <div className="px-4 pb-4 space-y-2 border-t border-white/5 pt-3">
                      <QField label="Book Length" value={q.book_length} />
                      <QField label="Synopsis" value={q.synopsis} />
                      {q.has_outline && <QField label="Outline" value={q.outline_summary} />}
                      {q.reference_books && <QField label="Reference Books" value={q.reference_books} />}
                      {q.special_requirements && <QField label="Special Requirements" value={q.special_requirements} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Cross-Posts */}
          {tab === 'crossposts' && (
            <div className="space-y-4" data-testid="admin-crossposts">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-muted-foreground">{crossPosts.length} cross-posts &middot; {crossPosts.filter(p => !p.synced_to_upost).length} pending</p>
              </div>
              {crossPosts.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Share2 className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No cross-posts yet. Blog posts will appear here automatically.</p>
                </div>
              )}
              {crossPosts.map(cp => (
                <div key={cp.post_id} className={`bg-white/5 border rounded-sm p-4 ${cp.synced_to_upost ? 'border-green-500/20' : 'border-white/10'}`} data-testid={`crosspost-${cp.post_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-sm truncate">{cp.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{cp.excerpt || cp.meta_description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${cp.synced_to_upost ? 'text-green-400 bg-green-500/10' : 'text-amber-400 bg-amber-500/10'}`}>
                          {cp.synced_to_upost ? 'Synced' : 'Pending'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{cp.source}</span>
                        {cp.tags && cp.tags.length > 0 && (
                          <span className="text-[10px] text-muted-foreground">{cp.tags.slice(0, 3).join(', ')}</span>
                        )}
                      </div>
                      {cp.synced_at && <p className="text-[10px] text-muted-foreground mt-1">Synced: {new Date(cp.synced_at).toLocaleString()}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!cp.synced_to_upost && (
                        <button onClick={() => syncCrossPost(cp.post_id)} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`sync-${cp.post_id}`}>
                          <CheckCircle className="w-3.5 h-3.5" /> Mark Synced
                        </button>
                      )}
                      <button onClick={() => exportCrossPost(cp.post_id)} className="flex items-center gap-1 text-xs text-blue-400 hover:underline" data-testid={`export-${cp.post_id}`}>
                        <Download className="w-3.5 h-3.5" /> Export
                      </button>
                      <a href={`/blog/${cp.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Analytics Tab */}
          {tab === 'analytics' && <AnalyticsTab headers={headers} />}

          {/* Email Drips Tab */}
          {tab === 'drips' && <DripsTab headers={headers} />}

          {/* Social Tab */}
          {tab === 'social' && <SocialTab headers={headers} />}

          {/* Payouts Tab */}
          {tab === 'payouts' && <PayoutsTab headers={headers} />}
        </div>
      </section>
    </>
  );
}

function QField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-xs text-foreground">{value}</p>
    </div>
  );
}
